<template>
  <div class="sp-quantum-select-panel">
    <div class="sp-quantum-select-panel__header">
      <sp-search-box
        v-if="!hideSearchBar"
        v-model="searchText"
        v-bind="$props"
        density="compact"
        @text-input="handleTextInput"
        @keydown.space.stop
        @keydown.enter.stop
      ></sp-search-box>
    </div>

    <div class="sp-quantum-select-panel__content">
      <sp-list
        v-if="hasItems"
        v-bind="$props"
        :items="items"
        scroll-selected-into-view
        @select-item="handleSelectItem"
        @update-selected="handleUpdateSelected"
        @append-action="handleAppendAction"
      ></sp-list>
      <div v-else-if="loading" class="sp-quantum-select-panel__loading">
        <sp-progress type="circular" size="x-large" aria-label="Loading"></sp-progress>
      </div>
      <div v-else-if="!hideNoData" class="sp-quantum-select-panel__content-no-result">
        <slot name="no-data">
          {{ noDataText }}
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * Panel renders a single level of a QuantumSelect: the search input, the list
 * of items for that level, and its loading and empty states. It holds no navigation state;
 * navigation containers (drilldown, flat, and so on) compose one or more panels.
 *
 * Private nested component of SpQuantumSelect; not a globally-registered custom element.
 */
import { computed, ref, watch } from "vue";
import { panelProps } from "#ce/components/SpQuantumSelect/props.js";
import { toBoolean } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";

const emit = defineEmits(["update-selected", "update-search", "next", "select-item"]);

const props = defineProps({
  ...panelProps,
  /**
   * Append-caret style for branch rows, forwarded to each sp-list-item. The
   * accordion container passes its host's `accordionCaretVariant`; drilldown
   * leaves the default "chevron".
   */
  caretVariant: {
    type: String,
    default: "chevron",
  },
  /**
   * Render branch-row carets with the in-place toggle mapping (down collapsed,
   * up expanded) instead of the drill-in mapping (right collapsed, down
   * expanded). Set by containers that expand rows in place (accordion).
   */
  caretToggle: {
    type: Boolean,
    default: false,
  },
});

// The field's v-model mirror. The `search` prop stays the container's
// programmatic channel (preset, clear), mirrored in through the watcher.
const searchText = ref(props.search ?? "");
watch(
  () => props.search,
  (value) => (searchText.value = value ?? ""),
);

const loading = computed(() => toBoolean(props.loading));
const hideNoData = computed(() => toBoolean(props.hideNoData));
const hideSearchBar = computed(() => toBoolean(props.hideSearchBar));
const isReadonly = computed(() => toBoolean(props.readonly));
const selectableBranches = computed(() => toBoolean(props.selectableBranches));
const branchToggleLabel = computed(() => props.branchToggleLabel);
const caretVariant = computed(() => props.caretVariant);
const caretToggle = computed(() => props.caretToggle);
const itemTestId = computed(() => props.itemTestId);

const items = computed(() =>
  props.items?.map((item) => {
    if (isObjectLike(item)) {
      return { ...item, props: extendItemProps(item) };
    }
    return item;
  }),
);

const hasItems = computed(() => items.value.length > 0);

function extendItemProps(item) {
  const props = {
    ...(item.props ?? {}),
  };
  // Shared row hook for the QA selector catalog; a per-item value wins.
  if (itemTestId.value != null && props["data-testid"] == null) {
    props["data-testid"] = itemTestId.value;
  }
  // Forward a top-level `disabled` flag so a disabled item shaped like
  // `{ title, value, disabled: true }` reaches sp-list as a per-item prop.
  // Coerce with toBoolean so string forms (e.g. "true") match sp-list's gate.
  if (toBoolean(item.disabled)) {
    props.disabled = true;
  }
  // sp-list-item reflects a removed string prop as "" (which fails its validator),
  // so the caret variant is always set to a valid value, even for leaf rows.
  props.appendCaretVariant = caretVariant.value;
  if (item.hasChildren) {
    // Render a rotating chevron caret (SpCaret) in the append region. In-place
    // containers (accordion) set `caretToggle` so the chevron points down and
    // rotates up when the row's `expanded` flag is set; navigation containers
    // (drilldown) leave both unset so it points right.
    props.appendCaret = true;
    props.appendCaretExpanded = !!item.expanded;
    props.appendCaretToggle = caretToggle.value;
    if (selectableBranches.value) {
      // Branch is a real selectable value; its children toggle from a dedicated
      // interactive caret (which swallows the click so selecting ≠ toggling).
      props.selectable = true;
      props.appendCaretInteractive = true;
      props.appendCaretLabel = branchToggleLabel.value;
    } else {
      // Classic accordion: the whole row toggles expansion and is not selectable.
      props.selectable = false;
    }
  }
  return props;
}

function handleTextInput({ detail }) {
  emit("update-search", ...detail);
}

function handleUpdateSelected({ detail }) {
  const [value] = detail;

  return emit("update-selected", value);
}

// The dedicated branch disclosure caret was clicked (selectableBranches mode).
// `sp-list` is a custom element, so its emit arrives as a CustomEvent whose
// detail carries the emit args (same shape handleSelectItem/handleUpdateSelected read).
// Toggle expansion via the same `next` channel the classic accordion uses; the
// click was swallowed by sp-list-item, so no selection fires alongside it.
function handleAppendAction({ detail }) {
  const [item] = detail;
  emit("next", item);
}

function handleSelectItem({ detail }) {
  const [item] = detail;

  // Classic accordion: a branch row toggles expansion. With selectableBranches
  // the row body selects instead and the dedicated caret (→ handleAppendAction)
  // owns toggling, so fall through to the normal selection path here.
  if (item?.hasChildren && !selectableBranches.value) {
    emit("next", item);
    return;
  }

  // Mirror SpList's selection gate: a readonly list or an item flagged
  // `selectable: false` / `disabled: true` updates nothing, so the full-item
  // event must not fire either — consumers route navigation/filters off it.
  // (SpList emits its raw `select-item` unconditionally so drill-in still works
  // for hasChildren rows, which are themselves `selectable: false`.)
  if (isReadonly.value || item?.props?.selectable === false || toBoolean(item?.props?.disabled)) {
    return;
  }

  // Clicking an already-selected row toggles it OFF — SpList emits its raw
  // `select-item` before applying that, and `update-selected` will clear the
  // value. So suppress the full-item event for a deselect; it should only fire
  // when the click actually selects/adds the item.
  if (isSelectedValue(item)) {
    return;
  }

  // Terminal (leaf) selection: surface the full item so the host can re-emit it
  // for consumers that need the whole payload, not just the selected value.
  emit("select-item", item);
}

// Whether the item's value is in the current selection (so a click on it would
// deselect rather than select). `props.selected` reflects the pre-click state.
function isSelectedValue(item) {
  const selected = Array.isArray(props.selected) ? props.selected : [];
  if (selected.length === 0) {
    return false;
  }
  const value = isObjectLike(item) ? item[props.itemValue] : item;
  return selected.some((entry) => String(entry) === String(value));
}
</script>

<style lang="scss" scoped>
sp-search-box {
  margin-inline: var(--quantum-select-content-padding);
}

.sp-quantum-select-panel {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  max-height: 100%;
  height: 100%;
  min-width: var(--quantum-select-popup-width);
}

.sp-quantum-select-panel__header:not(:empty) {
  margin-bottom: var(--sp-comp-quantum-select-panel-header-gap, var(--sp-ref-spacing-6, 0.75rem));
}

.sp-quantum-select-panel__content {
  position: relative;
  overflow-y: var(--sp-comp-quantum-select-panel-content-overflow-y, auto);
}

.sp-quantum-select-panel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.sp-quantum-select-panel__content-no-result {
  padding: var(--sp-comp-quantum-select-panel-no-result-padding, 1rem);
  color: var(--sp-comp-quantum-select-panel-no-result-color, #aaa);
}

sp-list {
  --list-item-inline-padding: var(--quantum-select-content-padding);
  // Inset the append region by the panel's content padding so the chevron
  // caret's right edge lines up with the search box and row dividers — mirroring
  // the prepend icon's left inset. (The previous `0` was calibrated for the old
  // `chevron-right-filled` glyph, whose internal SVG whitespace faked the inset;
  // SpCaret draws closer to its box edge, so it needs the real margin.)
  --sp-comp-list-item-append-margin-right: var(--quantum-select-content-padding);
  --sp-comp-list-item-prepend-icon-fill-hover: #777;
}
</style>
